import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Grid,
  TextField,
  makeStyles,
  Typography,
  FormControlLabel,
  Checkbox,
} from "@material-ui/core";
import { ToggleButton, ToggleButtonGroup } from "@material-ui/lab";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import Editor from "ckeditor5-custom-build/build/ckeditor";
import apiHelper from "../../../util/ApiHelper";
import { toast } from "react-toastify";
import editorConfiguration from "../../../util/EditorConfiguration";
import { useLoading } from "../../../util/LoadingProvider";
import UploadImage from "../UploadImage";

const schema = yup.object().shape({
  localizations: yup.object().shape({
    en: yup.object().shape({
      title: yup.string(),
      content: yup.string(),
      image: yup.string(),
      isExternalUrl: yup.boolean(),
      url: yup
        .string()
        .nullable()
        .optional()
        .matches(RegExp(/http:\/\/|https:\/\//g), {
          message: "Missing http:// or https://",
          excludeEmptyString: true,
        }),
    }),
    tc: yup.object().shape({
      title: yup.string(),
      content: yup.string(),
      image: yup.string(),
      isExternalUrl: yup.boolean(),
      url: yup
        .string()
        .nullable()
        .optional()
        .matches(RegExp(/http:\/\/|https:\/\//g), {
          message: "Missing http:// or https://",
          excludeEmptyString: true,
        }),
    }),
  }),
  status: yup.number(),
  type: yup.string(),
});

const defaultFormValues = {
  localizations: {
    en: {
      title: "",
      content: "",
      image: "",
      url: "",
      isExternalUrl: false,
    },
    tc: {
      title: "",
      content: "",
      image: "",
      url: "",
      isExternalUrl: false,
    },
  },
  type: "daily",
  status: 0,
  hkiffId: 3,
  id: 0,
};

const useStyles = makeStyles((theme) => ({
  textInput: {
    width: "100%",
    marginBottom: "12px",

    "& input": {
      fontSize: "14px",
    },
  },
  content: {
    "& .ck-editor__editable_inline": {
      minHeight: "362px",
    },
  },
  buttonGroup: {
    marginTop: "40px",
    textAlign: "left",
    "& button": {
      color: "#306F9D",
      fontSize: "13px",
      textTransform: "inherit",
    },
  },
  box: {
    padding: 0,
  },
  input: {
    width: "100%",
  },
}));

export default function FestivalDailyDetailPage({ disabled, ...props }) {
  const { match } = props;
  const id = match.params.id;
  const [defaultValues, setDefaultValues] = useState(defaultFormValues);
  const { setLoading } = useLoading();

  const classes = useStyles();

  const {
    control,
    handleSubmit,
    getValues,
    setValue,
    reset,
  } = useForm({
    defaultValues: React.useMemo(() => defaultValues, [defaultValues]),
    resolver: yupResolver(schema),
  });

  const onSubmit = (value) => {
    if (id !== "new") updateFestival(value);
    else createFestival(value);
  };

  const getFestival = React.useCallback(async () => {
    setLoading(true);
    try {
      const result = await apiHelper.hkiff.getFestivalById(id);
      console.log(result);
      setDefaultValues(result.data);
      reset(result.data);
    } catch (err) {
      console.log(err);
    }
    setLoading(false);
  }, [id, setLoading]);

  const updateFestival = async (value) => {
    try {
      const result = await apiHelper.hkiff.updateFestivalById(id, value);
      console.log(result);
      if (result.status === 200) {
        toast.dark("Your changes are saved.");
      }
    } catch (err) {
      console.log(err);
    }
  };

  const createFestival = async (value) => {
    try {
      const result = await apiHelper.hkiff.createFestival(value);
      console.log(result);
      if (result.status === 200) {
        toast.dark("The post is created.");
      }
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    if (id !== "new") getFestival();
  }, [getFestival, id]);

  return (
    <div className={classes.content}>
      <Box width="100%">
        <Typography variant="h4" gutterBottom>
          Festival Daily &gt; {id === "new" ? "Add New Post" : "Edit Post"}
        </Typography>
      </Box>
      <form onSubmit={handleSubmit(onSubmit)}>
        <fieldset disabled={disabled} className="fieldset">
          <Grid container xs={12} direction="row" spacing={3}>
            <Grid item xs={12}>
              <Controller
                name="localizations.en.title"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    label="Title (English)"
                    variant="outlined"
                    margin="dense"
                    className={classes.textInput}
                    fullWidth
                    {...field}
                    error={!!error}
                    helperText={error ? error.message : ""}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="localizations.tc.title"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    label="Title (Traditional Chinese)"
                    variant="outlined"
                    margin="dense"
                    className={classes.textInput}
                    fullWidth
                    {...field}
                    error={!!error}
                    helperText={error ? error.message : ""}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="type"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    label="Type"
                    variant="outlined"
                    margin="dense"
                    className={classes.textInput}
                    fullWidth
                    {...field}
                    error={!!error}
                    helperText={error ? error.message : ""}
                    InputProps={{
                      readOnly: true,
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>

          {defaultValues.localizations && (
            <>
              <Box
                fontWeight={900}
                fontSize={13}
                mb={2}
                style={{ marginTop: "30px" }}
                width="100%"
              >
                <Typography variant="p">Content (English)</Typography>
              </Box>

              <Grid container xs={12} direction="row" spacing={3}>
                <Grid item xs={12}>
                  <Controller
                    name="localizations.en.content"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <CKEditor
                        editor={Editor}
                        config={editorConfiguration}
                        data={field.value}
                        disabled={disabled}
                        onReady={(editor) => {
                          // You can store the "editor" and use when it is needed.
                          console.log("Editor is ready to use!", editor);
                          editor.setData(
                            defaultValues.localizations.en.content
                          );
                        }}
                        onChange={(event, editor) => {
                          const data = editor.getData();
                          field.onChange(data);
                          // console.log({ event, editor, data });
                        }}
                        onBlur={(event, editor) => {
                          console.log("Blur.", editor);
                        }}
                        onFocus={(event, editor) => {
                          console.log("Focus.", editor);
                        }}
                      />
                    )}
                  />
                </Grid>
              </Grid>

              <Box fontWeight={900} fontSize={13} mt={4} mb={2} width="100%">
                <Typography variant="p">
                  Content (Traditional Chinese)
                </Typography>
              </Box>

              <Grid container xs={12} direction="row" spacing={3}>
                <Grid item xs={12}>
                  <Controller
                    name="localizations.tc.content"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <CKEditor
                        editor={Editor}
                        config={editorConfiguration}
                        data={field.value}
                        disabled={disabled}
                        onReady={(editor) => {
                          // You can store the "editor" and use when it is needed.
                          console.log("Editor is ready to use!", editor);
                          editor.setData(
                            defaultValues.localizations.tc.content
                          );
                        }}
                        onChange={(event, editor) => {
                          const data = editor.getData();
                          field.onChange(data);
                          // console.log({ event, editor, data });
                        }}
                        onBlur={(event, editor) => {
                          console.log("Blur.", editor);
                        }}
                        onFocus={(event, editor) => {
                          console.log("Focus.", editor);
                        }}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </>
          )}

          <Grid
            container
            item
            xs={6}
            direction="row"
            spacing={3}
            style={{ marginTop: "32px" }}
          >
            <Grid item xs={12}>
              {defaultValues && (
                <UploadImage
                  name="localizations.en.image"
                  data={defaultValues}
                  control={control}
                  Controller={Controller}
                  setValue={setValue}
                  getValues={getValues}
                  disabled={disabled}
                  uploadLabel="Image (English)"
                  changeLabel="Image (English)"
                  uploadFolder="NEWS"
                />
              )}
            </Grid>
          </Grid>

          <Grid container item xs={6} direction="row" spacing={3}>
            <Grid item xs={12}>
              {defaultValues && (
                <UploadImage
                  name="localizations.tc.image"
                  data={defaultValues}
                  control={control}
                  Controller={Controller}
                  setValue={setValue}
                  getValues={getValues}
                  disabled={disabled}
                  uploadLabel="Image (Traditional Chinese)"
                  changeLabel="Image (Traditional Chinese)"
                  uploadFolder="NEWS"
                />
              )}
            </Grid>
          </Grid>

          <Grid container item xs={6} direction="row" spacing={3}>
            <Grid item xs={12}>
              <Controller
                name="localizations.en.isExternalUrl"
                control={control}
                render={({
                  field: { value, onChange, ...rest },
                  fieldState: { error },
                }) => {
                  return (
                    <FormControlLabel
                      control={
                        <Checkbox
                          style={{ marginLeft: "4px" }}
                          type="checkbox"
                          color="primary"
                          checked={value}
                          {...rest}
                          onChange={(event) => {
                            console.log(event.target.checked);
                            setValue(
                              "localizations.en.isExternalUrl",
                              event.target.checked
                            );
                            setValue(
                              "localizations.tc.isExternalUrl",
                              event.target.checked
                            );
                          }}
                        />
                      }
                      label="External URL"
                    />
                  );
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="localizations.en.url"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    label="URL (English)"
                    variant="outlined"
                    margin="dense"
                    className={classes.textInput}
                    fullWidth
                    {...field}
                    error={!!error}
                    helperText={error ? error.message : ""}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="localizations.tc.url"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    label="URL (Traditional Chinese)"
                    variant="outlined"
                    margin="dense"
                    className={classes.textInput}
                    fullWidth
                    {...field}
                    error={!!error}
                    helperText={error ? error.message : ""}
                  />
                )}
              />
            </Grid>
          </Grid>

          <Grid
            container
            item
            xs={12}
            spacing={3}
            style={{ marginTop: "52px", marginLeft: 0 }}
          >
            <Grid item xs={3} style={{ paddingLeft: "4px" }}>
              <Controller
                control={control}
                name="status"
                render={({
                  field: { value, onChange, ...rest },
                  fieldState: { error },
                }) => (
                  <ToggleButtonGroup
                    {...rest}
                    value={value}
                    onChange={(event, newFormats) => {
                      onChange(newFormats);
                    }}
                    exclusive
                    aria-label="Status"
                    className={classes.input}
                  >
                    <ToggleButton
                      value={1}
                      aria-label="Active"
                      style={{ width: "50%" }}
                      disabled={disabled}
                    >
                      Active
                    </ToggleButton>
                    <ToggleButton
                      value={0}
                      aria-label="Inactive"
                      style={{ width: "50%" }}
                      disabled={disabled}
                    >
                      Inactive
                    </ToggleButton>
                  </ToggleButtonGroup>
                )}
              />
            </Grid>
          </Grid>

          {disabled || (
            <div className={classes.buttonGroup}>
              <Button
                type="submit"
                variant="contained"
                disableElevation
                style={{
                  color: "white",
                  background: "#306F9D",
                  marginRight: "5px",
                  padding: "9px 16px",
                }}
              >
                Publish
              </Button>
            </div>
          )}
        </fieldset>
      </form>
    </div>
  );
}
